/**
 * Zentrale Solo-Logik für Admin-Akte und Mandanten-App.
 * Solo-Modus (isSolo / forceSoloTarif) steuert Preis UND Kalkulation.
 * forceSoloTarif = Admin-Override (Solo erzwingen, auch bei vorhandenen Mitarbeitern).
 */

export const readForceSoloTarif = (source: any): boolean => {
    if (!source) return false;
    if (source.forceSoloTarif === true) return true;
    if (source.clientData?.forceSoloTarif === true) return true;
    return false;
};

/** Explizites isSolo-Flag (Root oder clientData). */
export const readIsSoloFlag = (source: any): boolean | undefined => {
    if (!source) return undefined;
    if (source.isSolo === true || source.clientData?.isSolo === true) return true;
    if (source.isSolo === false || source.clientData?.isSolo === false) return false;
    return undefined;
};

export const getProductivePersonalData = (clientData: any): any[] => {
    const list = clientData?.personalData;
    return Array.isArray(list) ? list : [];
};

/**
 * Effektiver Solo-Modus in der Mandanten-App (Kalkulator, Navigation, Gesamtleistung).
 * Reihenfolge:
 * 1) forceSoloTarif / isSolo === true → Solo (gesamte Kalkulation)
 * 2) isSolo === false → Team (auch bei leerer personalData)
 * 3) Legacy-Fallback: keine Mitarbeiter → Solo
 */
export const isSoloModeActive = (clientData: any): boolean => {
    if (readForceSoloTarif(clientData)) return true;
    const isSoloFlag = readIsSoloFlag(clientData);
    if (isSoloFlag === true) return true;
    if (clientData?.isSoloBusiness === true) return true;
    if (clientData?.insiderSoloChecked === true) return true;
    if (isSoloFlag === false) return false;
    return getProductivePersonalData(clientData).length === 0;
};

export type PeoPrefixLetter = 'B' | 'S' | 'T';

/** Liest den Buchstaben aus einer vergebenen System-ID (PEO-T-… / PEO-S-… / PEO-B-…). */
export const readPeoLetterFromMandantNr = (nr: string | null | undefined): PeoPrefixLetter | null => {
    if (!nr || typeof nr !== 'string') return null;
    try {
        const match = nr.match(/PEO-([BST])/i);
        if (!match) return null;
        return match[1].toUpperCase() as PeoPrefixLetter;
    } catch {
        return null;
    }
};

/** Solo-Tarif für Admin (Preis, Badge): Override, sonst vergebene PEO-Nummer, sonst personalData. */
export const isSoloTariffForAdmin = (client: any, forceSoloOverride?: boolean): boolean => {
    const forced = forceSoloOverride !== undefined
        ? forceSoloOverride
        : readForceSoloTarif(client);
    if (forced) return true;
    if (!client) return false;

    // Vergebene System-ID schlägt Auto-Ableitung aus (noch) leerer personalData
    const letter = readPeoLetterFromMandantNr(client.mandantNr || client.clientData?.mandantNr);
    if (letter === 'T' || letter === 'B') return false;
    if (letter === 'S') return true;

    // Vorabkunden: kein Auto-Solo aus leerer personalData — nur explizites Flag
    if (client.isLegacy === true || client.clientData?.isLegacy === true) {
        return client.isSolo === true;
    }
    const personal = client.clientData?.personalData || client.personalData || [];
    return !Array.isArray(personal) || personal.length === 0;
};

/**
 * Einheitlicher System-ID Wasserfall:
 * 1) forceSoloTarif → S
 * 2) basic → B
 * 3) insider → T wenn personalData.length > 0, sonst S
 */
export const resolvePeoPrefixLetter = (opts: {
    forceSoloTarif?: boolean;
    lizenzTyp?: string;
    client?: any;
    personalData?: any[];
}): PeoPrefixLetter => {
    const forced = opts.forceSoloTarif !== undefined
        ? opts.forceSoloTarif === true
        : readForceSoloTarif(opts.client || {});
    if (forced) return 'S';

    const typRaw =
        opts.lizenzTyp ||
        opts.client?.lizenzDetails?.typ ||
        opts.client?.clientData?.lizenzDetails?.typ ||
        '';
    const typ = String(typRaw).toLowerCase();

    if (typ.includes('basic')) return 'B';

    if (typ.includes('insider')) {
        const personal =
            opts.personalData ??
            opts.client?.clientData?.personalData ??
            opts.client?.personalData ??
            [];
        return Array.isArray(personal) && personal.length > 0 ? 'T' : 'S';
    }

    return 'B';
};

export const buildPeoPrefix = (letter: PeoPrefixLetter): string => `PEO-${letter}-`;
